import IcImage from '@/assets/icons/ic-image.svg';
import { cn } from '@/lib';
import { formatCurrency } from '@/lib/currency';
import {
    type FunnelPageRelationship,
    type FunnelPageSlot,
    getFunnelPageBranchTag,
    getFunnelPageRelationshipLabel,
    sortFunnelPageBranches,
} from '../../../logic';
import type { FunnelPageProductInfo } from '../../../types';
import type { JourneyStep } from './funnelJourney.logic';

// ─── Revenue card (sits above the nav panel in the left column) ───────────────

interface FunnelJourneyRevenueCardProps {
    earnedCents: number;
    currency: string;
    revenueJustIncreased: boolean;
}

export const FunnelJourneyRevenueCard = ({
    earnedCents,
    currency,
    revenueJustIncreased,
}: FunnelJourneyRevenueCardProps) => {
    const earnedLabel = formatCurrency(earnedCents / 100, currency);
    const hasEarned = earnedCents > 0;

    return (
        <div className='bg-canvas border-neutral-subtle shadow-stan relative overflow-hidden rounded-2xl border p-6'>
            <div className='flex flex-col items-center gap-2 text-center'>
                <p className='text-caption-2-bold text-gray tracking-wider uppercase'>Earnings</p>
                <div className='relative'>
                    <p
                        key={earnedCents}
                        className={cn(
                            'text-title-display-1 font-extrabold tabular-nums transition-colors duration-300',
                            hasEarned ? 'text-brand-main' : 'text-dark',
                            revenueJustIncreased && 'animate-revenue-pop'
                        )}
                    >
                        {earnedLabel}
                    </p>
                    {revenueJustIncreased && hasEarned && (
                        <span
                            key={`glow-${earnedCents}`}
                            aria-hidden
                            className='bg-brand-main/20 animate-revenue-glow pointer-events-none absolute inset-0 -z-10 rounded-full blur-2xl'
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Nav panel ────────────────────────────────────────────────────────────────

interface FunnelJourneyNavPanelProps {
    navSlots: FunnelPageSlot[];
    currentPageId: number | null;
    visitedIds: Set<number>;
    history: JourneyStep[];
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    positionByPageId: Map<number, number>;
    relationshipByPageId: Map<number, FunnelPageRelationship>;
    isComplete: boolean;
    onExit: () => void;
}

export const FunnelJourneyNavPanel = ({
    navSlots,
    currentPageId,
    visitedIds,
    history,
    productInfoByPageId,
    positionByPageId,
    relationshipByPageId,
    isComplete,
    onExit,
}: FunnelJourneyNavPanelProps) => {
    const totalSteps = positionByPageId.size;
    const currentStep = currentPageId !== null ? (positionByPageId.get(currentPageId) ?? null) : null;

    return (
        <div className='bg-canvas shadow-stan flex w-full flex-col gap-4 rounded-2xl p-4 md:gap-6 md:p-7'>
            {/* Header */}
            <div className='flex items-center justify-between gap-3'>
                <div className='flex flex-col gap-0.5'>
                    <span className='text-heading-h3 text-dark'>
                        {isComplete
                            ? 'Journey complete'
                            : currentStep !== null
                              ? `Page ${currentStep} of ${totalSteps}`
                              : 'Preview'}
                    </span>
                    <span className='text-body-xs text-gray font-semibold'>Buyer journey preview</span>
                </div>
                <button
                    type='button'
                    onClick={onExit}
                    className='border-neutral-primary text-label-xs-bold text-gray hover:bg-neutral-subtle cursor-pointer rounded-full border px-3 py-1 transition-colors'
                >
                    Exit
                </button>
            </div>

            {/* Page list */}
            <div className='flex flex-col gap-1.5'>
                {navSlots.map(slot => (
                    <NavTreeNode
                        key={slot.kind === 'page' ? slot.page.page_id : `s-${slot.stepNumber}`}
                        slot={slot}
                        currentPageId={currentPageId}
                        visitedIds={visitedIds}
                        history={history}
                        productInfoByPageId={productInfoByPageId}
                        positionByPageId={positionByPageId}
                        relationshipByPageId={relationshipByPageId}
                        parentStepNumber={null}
                        isComplete={isComplete}
                    />
                ))}
            </div>
        </div>
    );
};

// ─── Internal tree node ───────────────────────────────────────────────────────

type PageSlot = Extract<FunnelPageSlot, { kind: 'page' }>;

interface NavTreeNodeProps {
    slot: FunnelPageSlot;
    currentPageId: number | null;
    visitedIds: Set<number>;
    history: JourneyStep[];
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    positionByPageId: Map<number, number>;
    relationshipByPageId: Map<number, FunnelPageRelationship>;
    parentStepNumber: number | null;
    isComplete: boolean;
}

const NavTreeNode = ({
    slot,
    currentPageId,
    visitedIds,
    history,
    productInfoByPageId,
    positionByPageId,
    relationshipByPageId,
    parentStepNumber,
    isComplete,
}: NavTreeNodeProps) => {
    if (slot.kind !== 'page') return null;

    const { page } = slot;
    const relationship = relationshipByPageId.get(page.page_id);
    const isBranch = relationship?.kind === 'upsell' || relationship?.kind === 'downsell';
    const branchTag = isBranch ? getFunnelPageBranchTag(relationship) : undefined;
    const subtitle = !isBranch ? getFunnelPageRelationshipLabel(relationship) : undefined;
    const stepNumber = positionByPageId.get(page.page_id) ?? slot.stepNumber;
    const isCurrent = !isComplete && page.page_id === currentPageId;
    const isVisited = visitedIds.has(page.page_id);
    const historyEntry = history.find(s => s.page.page_id === page.page_id);
    const productInfo = productInfoByPageId[page.page_id];
    const title = productInfo?.title ?? page.title;
    const priceLabel =
        typeof productInfo?.price === 'number' && productInfo.price > 0
            ? formatCurrency(productInfo.price, productInfo.currency ?? 'USD')
            : '';

    const branchChildren = (slot.children ?? []).filter((c): c is PageSlot => c.kind === 'page');
    const sortedBranches = sortFunnelPageBranches(branchChildren, relationshipByPageId);

    return (
        <div className='flex flex-col gap-1.5'>
            <div
                className={cn(
                    'group flex min-w-0 items-center gap-2 rounded-lg px-2 py-2 text-left transition-all duration-150',
                    isCurrent ? 'bg-brand-subtle' : !isVisited && !isComplete ? 'opacity-40' : undefined
                )}
            >
                {/* Step number */}
                <span className='text-caption-2-bold text-gray w-4 shrink-0 text-center tabular-nums'>
                    {stepNumber}
                </span>

                {/* Thumbnail */}
                <div
                    className={cn(
                        'bg-canvas flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border transition-all',
                        isCurrent ? 'border-brand-main ring-brand-selected ring-2' : 'border-neutral-subtle'
                    )}
                >
                    {productInfo?.image ? (
                        <img src={productInfo.image} alt='' className='size-full object-cover' />
                    ) : (
                        <IcImage className='text-gray size-5' />
                    )}
                </div>

                {/* Text */}
                <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                    <div className='flex min-w-0 items-center gap-1.5'>
                        {branchTag ? (
                            <>
                                <span
                                    className={cn('text-label-xs-bold! font-extrabold uppercase', branchTag.colorClass)}
                                >
                                    {branchTag.label}
                                </span>
                                {parentStepNumber !== null && (
                                    <span className='text-caption-2-bold text-gray shrink-0 -translate-x-0.5'>
                                        from Page {parentStepNumber}
                                    </span>
                                )}
                            </>
                        ) : subtitle ? (
                            <span className='text-caption-2-bold text-gray truncate uppercase'>{subtitle}</span>
                        ) : null}
                    </div>
                    <span className='text-heading-h5 text-dark truncate'>{title || 'Untitled'}</span>
                </div>

                {/* Right: price + action badge */}
                <div className='flex shrink-0 flex-col items-end gap-0.5'>
                    {priceLabel && <span className='text-label-sm-bold text-dark'>{priceLabel}</span>}
                    {isVisited && historyEntry ? (
                        <span
                            className={cn(
                                'text-caption-3-bold shrink-0 rounded px-1.5 py-0.5 text-[10px] leading-none uppercase',
                                historyEntry.action === 'buy'
                                    ? 'bg-success-soft text-success'
                                    : 'bg-neutral-soft text-dark'
                            )}
                        >
                            {historyEntry.action === 'buy' ? 'Purchased' : 'Skipped'}
                        </span>
                    ) : null}
                </div>
            </div>

            {sortedBranches.length > 0 && (
                <div className='border-border-subtle ml-3 flex flex-col gap-1.5 border-l pl-3'>
                    {sortedBranches.map(branch => (
                        <NavTreeNode
                            key={branch.page.page_id}
                            slot={branch}
                            currentPageId={currentPageId}
                            visitedIds={visitedIds}
                            history={history}
                            productInfoByPageId={productInfoByPageId}
                            positionByPageId={positionByPageId}
                            relationshipByPageId={relationshipByPageId}
                            parentStepNumber={stepNumber}
                            isComplete={isComplete}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
